import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchEntityListAction } from "../../actions/entityListActions";
import { updateEntityAction } from "../../actions/updateEntityAction";
import { approveEntityAction } from "../../actions/approveEntityAction";
import { deficiencyEntityAction } from "../../actions/deficiencyEntityAction";
import { getRequiredDocuments } from "./AllDataFields";
import { usePermissions } from "../../utils/roleUtils";
import { resendKycLinkAction } from "../../actions/resendKycLinkAction";
import { deleteEntityAction } from "../../actions/deleteEntityAction";
import {
  monthsAgo,
  today,
  validateDateRange,
} from "../../utils/dateRangeUtils";
import { fetchAllEntitiesForDownloadAction } from "actions/fetchAllEntitiesForDownloadAction";
import { ALL_COLS, toCSV, triggerDownload } from "utils/downloadUtils";

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const truncate = (text, maxLen = 10) =>
  text && text.length > maxLen ? `${text.slice(0, maxLen)}...` : text || "—";

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const DateOrPending = ({ iso }) => {
  const d = fmtDate(iso);
  return d ? (
    <span className="col-date">{d}</span>
  ) : (
    <span className="status-badge pending">Pending</span>
  );
};

const EMPTY_PAGINATION = (size = 10) => ({
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: size,
  hasNextPage: false,
  hasPreviousPage: false,
});

export const entityCategoryOptions = [
  { value: "", label: "Select Entity Category" },
  { value: "client", label: "Client" },
  { value: "Service Partner", label: "Service Partner" },
];
export const msmeStatusOptions = [
  { value: "", label: " Select MSME Status" },
  { value: "false", label: "No" },
  { value: "true", label: "Yes" },
];

export const documentStatusOptions = [
  { value: "", label: "All Documents Status" },
  { value: "pending", label: "Pending" },
  { value: "Done", label: "Done" },
];

export const approveStatusOptions = [
  { value: "", label: "Select Approval Status" },
  { value: "false", label: "Pending" },
  { value: "true", label: "Approved" },
];

export const lobTypeOptions = [
  { value: "", label: "Select LOB Type" },
  { value: "travel", label: "Travel" },
  { value: "corporate", label: "Corporate" },
  { value: "auto", label: "Auto" },
  { value: "gadget", label: "Gadget" },
  { value: "travel_service", label: "Travel Service" },
  { value: "auto_service", label: "Auto Service" },
  { value: "gadget_service", label: "Gadget Service" },
  { value: "mobile_service", label: "Mobile Service" },
];

const DocStatusBadge = ({ documents = [] }) => {
  const uploaded = documents.some((d) => d.files?.some((f) => f.isUploaded));
  return (
    <span className={`status-badge ${uploaded ? "done" : "pending"}`}>
      {uploaded ? "Done" : "Pending"}
    </span>
  );
};

export const DocListInline = ({ documents = [], entityData = {} }) => {
  const required = getRequiredDocuments(entityData);
  if (!required.length) return <span className="doc-none">—</span>;
  return (
    <div className="doc-list-inline">
      {required.map((req) => {
        const uploaded = documents.some(
          (d) => d.key === req.key && d.files?.some((f) => f.isUploaded),
        );
        return (
          <span
            key={req.key}
            className={`doc-chip ${uploaded ? "doc-chip--done" : "doc-chip--pending"}`}
          >
            {uploaded ? "✓" : "○"} {req.label}
          </span>
        );
      })}
    </div>
  );
};

// ── Ledger status column (Finance only) ──────────────────────────────────────
const LedgerStatusBadge = ({ row }) => {
  const isLedger = row.entity?.isLedger; // "yes" | "no" | null
  const isLedgerUploaded = row.entity?.isLedgerUploaded; // true | false
  // conso÷ale.log("IsLeder : ", isLedger);
  // null/undefined → not yet configured
  if (isLedger === null || isLedger === "null" || isLedger === undefined) {
    return <span className="status-badge default">Not Selected</span>;
  }

  // "no" → not required
  if (isLedger === "no") {
    return <span className="status-badge pending">Not Required</span>;
  }

  // "yes" + uploaded → done
  if (isLedger === "yes" && isLedgerUploaded === true) {
    return <span className="status-badge approved">Uploaded</span>;
  }

  // "yes" + not uploaded → pending
  return <span className="status-badge uploaded">Pending</span>;
};

const BASE_COLUMNS = [
  {
    header: "Entity ID",
    accessor: "entityId",
    render: (row) => (
      <span className="col-entity-id">{row.entity?.entityId || "—"}</span>
    ),
  },
  {
    header: "Entity Name",
    accessor: "entityName",
    render: (row) => (
      <div>
        <div className="col-primary">{row.entity?.entityName || "—"}</div>
        <div className="col-sub">{row.entity?.tradeName || ""}</div>
      </div>
    ),
  },
  {
    header: "Department",
    accessor: "department",
    render: (row) => row.entity?.aaDepartmentFunction || "—",
  },
  {
    header: "Parent",
    accessor: "parentCode",
    render: (row) => row.entity?.parentCompanyName || "—",
  },

  {
    header: "Category / LOB",
    accessor: "entityCategory",
    render: (row) => {
      const cat = row.entity?.entityCategory || "";
      const label =
        cat === "client"
          ? "Client"
          : cat === "Service Partner"
            ? "Service Partner"
            : cat || "—";
      return (
        <div>
          <div className="col-primary">{label}</div>
          <div className="col-sub">{row.entity?.lobType || ""}</div>
        </div>
      );
    },
  },

  // {
  //   header: "Contact",
  //   accessor: "emailAddress",
  //   render: (row) => (
  //     <div>
  //       <div className="col-primary" title={row.entity?.entityName || ""}>
  //         {truncate(row.entity?.entityName, 14)}
  //       </div>
  //       <div className="col-sub" title={row.entity?.emailAddress || ""}>
  //         {truncate(row.entity?.emailAddress, 14)}
  //       </div>
  //     </div>
  //   ),
  // },
  // {
  //   header: "Documents",
  //   accessor: "documentsStatus",
  //   render: (row) => <DocStatusBadge documents={row.documents || []} />,
  // },
  {
    header: " Status",
    accessor: "vendorStatus",
    render: (row) => {
      const {
        isVerified,
        requireKYC,
        isDocumentUploaded,
        isResendLink,
        folderNo,
        isDeficiency,
      } = row.entity || {};

      if (folderNo != null && isVerified === true) {
        return <span className="status-badge legacy">Legacy Record</span>;
      }

      // Fully approved
      if (isVerified === true) {
        return <span className="status-badge approved">Approved</span>;
      }

      // KYC link was resent — case reassigned
      if (isDeficiency === true || isResendLink === true) {
        return <span className="status-badge reassigned"> Reassigned</span>;
      }

      // KYC required and documents uploaded
      if (requireKYC === true && isDocumentUploaded === true) {
        return <span className="status-badge uploaded"> Uploaded</span>;
      }
      // if (folderNo !== null && isVerified === true) {
      //   return <span className="status-badge uploaded"> Legacy Record</span>;
      // }

      // Not yet started / KYC not required / docs not uploaded
      return <span className="status-badge pending">Case Initiated</span>;
    },
  },
  //make the 3 colmn data
  // {
  //   header: "Created On",
  //   accessor: "primarycreatedat",
  //   render: (row) => <DateOrPending iso={row.entity?.createdOn} />,
  // },
  // {
  //   header: "kyc On",
  //   accessor: "documentationUploadedOn",
  //   render: (row) => <DateOrPending iso={row.entity?.kycCreatedOn} />,
  // },
  // {
  //   header: "Approved On",
  //   accessor: "approvedOn",
  //   render: (row) => <DateOrPending iso={row.entity?.isVerified} />,
  // },
];

// Finance-only ledger column
const LEDGER_COLUMN = {
  header: "Ledger Status",
  accessor: "ledger",
  render: (row) => <LedgerStatusBadge row={row} />,
};

// MSME column — shows registration status and msmeStatus value when present
const MSME_COLUMN = {
  header: "MSME",
  accessor: "msme",
  render: (row) => {
    const isRegistered = row.entity?.isRegisteredUnderMSME;
    const msmeStatus = row.entity?.msmeStatus;
    return (
      <div>
        <div>
          {isRegistered === true ? (
            <span className="status-badge approved">Yes</span>
          ) : (
            <span className="status-badge pending">No</span>
          )}
        </div>
        {/* {msmeStatus && <div className="col-sub">{msmeStatus}</div>} */}
      </div>
    );
  },
};

// Export as a function so Finance role can inject the ledger + MSME columns
export const getEntityListColumns = (isFinance = false) =>
  isFinance
    ? [...BASE_COLUMNS, MSME_COLUMN, LEDGER_COLUMN]
    : [...BASE_COLUMNS, MSME_COLUMN];

// Keep backward-compat export (non-Finance default)
export const entityListColumns = [...BASE_COLUMNS, MSME_COLUMN];

export const useEntityList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const permissions = usePermissions();
  const resolvedInitialApprove =
    urlParams.get("approveStatus") || permissions.defaultApproveStatus || "";

  const [currentPage, setCurrentPage] = useState(1);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [entities, setEntities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION());
  const [searchQuery, setSearchQuery] = useState("");
  const [entityCategory, setEntityCategory] = useState(
    urlParams.get("entityCategory") || "",
  );
  const [documentStatus, setDocumentStatus] = useState(
    urlParams.get("documentStatus") || "",
  );
  const [approveStatus, setApproveStatus] = useState(resolvedInitialApprove);
  const [lobType, setLobType] = useState("");
  const [aaDepartmentFunction, setAaDepartmentFunction] = useState("");
  const [msmeStatus, setMsmeStatus] = useState("");
  const [requireKYC, setRequireKYC] = useState(
    urlParams.get("requireKYC") || "", // "true", "false", or ""
  );
  const [dateFrom, setDateFrom] = useState(monthsAgo(1));
  const [dateTo, setDateTo] = useState(today());
  const [dateError, setDateError] = useState(null);
  const [pageSize, setPageSize] = useState(10);

  const debounceTimer = useRef(null);
  const isFirstRender = useRef(true);
  const prevSearchRef = useRef("");
  const isLoadingRef = useRef(false);

  const buildFilters = useCallback(
    (query, category, status, approved, lob, from, to, msme, kyc, dept) => {
      const filters = {};
      if (query) {
        filters.entityName = query.trim();
        filters.emailAddress = query.trim();
      }
      if (category) filters.entityCategory = category;
      if (status) filters.status = status;
      if (lob) filters.lobType = lob;
      if (dept) filters.aaDepartmentFunction = dept;
      if (from) filters.dateFrom = from;
      if (to) filters.dateTo = to;
      filters.isVerified =
        approved === "true" ? true : approved === "false" ? false : null;
      filters.isRegisteredUnderMSME =
        msme === "true" ? true : msme === "false" ? false : null;
      filters.requireKYC =
        kyc === "true" ? true : kyc === "false" ? false : null;
      return filters;
    },
    [],
  );

  const loadEntityList = useCallback(
    async (
      page = 1,
      query = "",
      category = "",
      status = "",
      approved = "",
      size = 10,
      lob = "",
      from = "",
      to = "",
      msme = "",
      kyc = "",
      dept = "",
    ) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const filters = buildFilters(
          query,
          category,
          status,
          approved,
          lob,
          from,
          to,
          msme,
          kyc,
          dept,
        );
        const response = await fetchEntityListAction(page, size, filters);
        if (response.success) {
          const data = response.data || [];

          const sp = response.pagination || {};
          setPagination({
            ...sp,
            totalItems: sp.totalItems || data.length,
            totalPages:
              sp.totalPages || Math.ceil((sp.totalItems || data.length) / size),
            hasNextPage: sp.hasNextPage ?? page < (sp.totalPages || 1),
            hasPreviousPage: sp.hasPreviousPage ?? page > 1,
          });
          setEntities(data);
        } else {
          toast.error(response.message || "Failed to load entity list");
          setEntities([]);
          setPagination(EMPTY_PAGINATION(size));
        }
      } catch {
        toast.error("Failed to load entity list");
        setEntities([]);
        setPagination(EMPTY_PAGINATION(size));
      } finally {
        setIsLoading(false);
        isLoadingRef.current = false;
      }
    },
    [buildFilters],
  );

  // Debounce search (400ms), immediate for all other filter/page changes
  useEffect(() => {
    let cancelled = false;
    const isSearchChange =
      !isFirstRender.current && prevSearchRef.current !== searchQuery;
    prevSearchRef.current = searchQuery;
    isFirstRender.current = false;

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(
      () => {
        if (!cancelled) {
          // When requireKYC filter is active (from dashboard card click),
          // send ONLY requireKYC — all other filters null
          if (requireKYC !== "") {
            loadEntityList(
              currentPage,
              "",
              "",
              "",
              "",
              pageSize,
              "",
              "",
              "",
              "",
              requireKYC,
              "",
            );
          } else {
            loadEntityList(
              currentPage,
              searchQuery,
              entityCategory,
              documentStatus,
              approveStatus,
              pageSize,
              lobType,
              dateFrom,
              dateTo,
              msmeStatus,
              "",
              aaDepartmentFunction,
            );
          }
        }
      },
      isSearchChange ? 400 : 0,
    );

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer.current);
    };
  }, [
    currentPage,
    searchQuery,
    entityCategory,
    documentStatus,
    approveStatus,
    pageSize,
    lobType,
    aaDepartmentFunction,
    dateFrom,
    dateTo,
    msmeStatus,
    requireKYC,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetPage = () => setCurrentPage(1);
  const handleEdit = (entityRow, viewOnly = false) => {
    setSelectedClient({ ...entityRow, viewOnly });
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedClient(null);
  };

  const handleSaveClient = async (updatedData) => {
    const response = await updateEntityAction(updatedData.id, updatedData);
    if (response.success) {
      toast.success("Entity updated successfully!");
      await loadEntityList(currentPage);
      return response;
    }
    toast.error(response.message || "Failed to update entity");
    throw new Error(response.message);
  };

  const handleApproveClient = async ({ entityPrimaryId, email, spocEmail }) => {
    // final approval api call to this
    const response = await approveEntityAction({
      entityPrimaryId,
      email,
      spocEmail,
    });
    if (response.success) {
      toast.success(response.message || "Entity approved!");
      await loadEntityList(currentPage);
    } else {
      toast.error(response.message || "Failed to approve entity");
    }
    return response;
  };

  const handleDeficiencyClient = async ({
    entityPrimaryId,
    entityId,
    entityName,
    emailAddress,
    spocEmail,
    deficiencies,
    defCreatedBy,
  }) => {
    const response = await deficiencyEntityAction({
      entityPrimaryId,
      entityId,
      entityName,
      emailAddress,
      spocEmail,
      deficiencies,
      defCreatedBy,
    });
    if (response.success) {
      toast.success(response.message || "Deficiency submitted!");
      await loadEntityList(currentPage);
    } else {
      toast.error(response.message || "Failed to submit deficiency");
    }
    return response;
  };

  const handleDelete = async (entityRow) => {
    const id = entityRow.entity?.entityPrimaryId || entityRow.entity?.id;
    if (!id) {
      toast.error("Entity ID not found");
      return;
    }
    const result = await deleteEntityAction(id);
    if (result.success) {
      toast.success(result.message || "Entity deleted successfully!");
      await loadEntityList(currentPage);
    } else {
      toast.error(result.message || "Failed to delete entity");
    }
  };

  const handleResendClient = async (entityRow) => {
    const id = entityRow.entity?.entityPrimaryId || entityRow.entity?.id;
    if (!id) {
      toast.error("Entity ID not found");
      return;
    }
    const result = await resendKycLinkAction(id);
    if (result.success) {
      toast.success(result.message || "KYC link resent!");
    } else {
      toast.error(result.message || "Failed to resend KYC link");
    }
  };

  const handleClearFilter = () => {
    resetPage();
    setSearchQuery("");
    setEntityCategory("");
    setDocumentStatus("");
    setApproveStatus("");
    setLobType("");
    setAaDepartmentFunction("");
    setMsmeStatus("");
    setRequireKYC("");
    setDateFrom(monthsAgo(1));
    setDateTo(today());
    setDateError(null);
    setPageSize(10);
  };

  return {
    // state
    currentPage,
    isEditModalOpen,
    selectedClient,
    currentEntities: entities,
    isLoading,
    pageSize,
    pagination,
    // filter state + setters
    searchQuery,
    setSearchQuery: (v) => {
      resetPage();
      setSearchQuery(v);
    },
    entityCategory,
    setEntityCategory: (v) => {
      resetPage();
      setEntityCategory(v);
    },
    documentStatus,
    setDocumentStatus: (v) => {
      resetPage();
      setDocumentStatus(v);
    },
    approveStatus,
    setApproveStatus: (v) => {
      resetPage();
      setApproveStatus(v);
    },
    msmeStatus,
    setMsmeStatus: (v) => {
      resetPage();
      setMsmeStatus(v);
    },
    requireKYC,
    setRequireKYC: (v) => {
      resetPage();
      setRequireKYC(v);
    },
    lobType,
    setLobType: (v) => {
      resetPage();
      setLobType(v);
      setAaDepartmentFunction(""); // reset dept when LOB changes
    },
    aaDepartmentFunction,
    setAaDepartmentFunction: (v) => {
      resetPage();
      setAaDepartmentFunction(v);
    },
    dateFrom,
    setDateFrom: (v) => {
      const err = validateDateRange(v, dateTo);
      setDateError(err);
      if (!err) resetPage();
      setDateFrom(v);
    },
    dateTo,
    setDateTo: (v) => {
      const err = validateDateRange(dateFrom, v);
      setDateError(err);
      if (!err) resetPage();
      setDateTo(v);
    },
    dateError,
    // actions
    handleEdit,
    handleCloseModal,
    handleSaveClient,
    handleApproveClient,
    handleDeficiencyClient,
    handleDelete,
    handleResendClient,
    handleClearFilter,
    handleLoadMore: () => setPageSize((p) => p + 10),
    handleLoadLess: () => setPageSize((p) => Math.max(10, p - 10)),
    setCurrentPage: (page) => {
      if (page !== currentPage && page >= 1) setCurrentPage(page);
    },
  };
};

// TODO: make the otp verifucation then can be download data
// export const downloadEntityData = async (filters = {}) => {
//   // Step 1: Ask for OTP first
//   const otp = await showOtpModal(); // returns OTP string or null if cancelled

//   if (!otp) return; // user cancelled

//   // Step 2: Verify OTP
//   const { success: otpSuccess, error: otpError } = await verifyOtpAction(otp);

//   if (!otpSuccess) {
//     alert(otpError || "OTP verification failed. Please try again.");
//     return;
//   }

//   // Step 3: OTP verified → now fetch data
//   const { success, data, error } =
//     await fetchAllEntitiesForDownloadAction(filters);

//   if (!success || !data.length) {
//     alert(error ? `Download failed: ${error}` : "No data to download.");
//     return;
//   }

//   // Step 4: Download
//   const ts = new Date().toISOString().slice(0, 10);
//   triggerDownload(toCSV(data, ALL_COLS), `entities_${ts}.csv`);
// };

export const downloadEntityData = async (filters = {}) => {
  const { success, data, error } =
    await fetchAllEntitiesForDownloadAction(filters);

  if (!success || !data.length) {
    alert(error ? `Download failed: ${error}` : "No data to download.");
    return;
  }

  const ts = new Date().toISOString().slice(0, 10);
  triggerDownload(toCSV(data, ALL_COLS), `entities_${ts}.csv`);
};
